/**
 * Reusable "Share the app" button — opens the native share sheet on mobile
 * (navigator.share) and falls back to WhatsApp + copy-to-clipboard on desktop.
 * Meant to sit inside any header/action bar without owning its own layout.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { Share2, MessageCircle, Copy, Loader2, Mail } from "lucide-react";

import { useLang } from "@/context/LangContext";

const originOf = () =>
  typeof window === "undefined" ? "" : window.location.origin;

const SHARE_TEXTS = {
  es: {
    title: "Canales Furniture · Familia",
    body:
      "Únete a la revista interactiva de Canales Furniture. Aprende con cómics, gana insignias y muéstranos cómo cierras ventas 💪🔥",
  },
  en: {
    title: "Canales Furniture · Family",
    body:
      "Join the Canales Furniture interactive magazine. Learn with comics, earn badges and show us how you close 💪🔥",
  },
};

const openWhatsapp = (text) => {
  if (typeof window === "undefined") return;
  const encoded = encodeURIComponent(text);
  window.open(`https://wa.me/?text=${encoded}`, "_blank", "noopener");
};

const openMail = (subject, body) => {
  if (typeof window === "undefined") return;
  const url = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.location.href = url;
};

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  }
};

export default function ShareAppButton({
  label,
  variant = "primary",
  size = "md",
  className = "",
  url,
}) {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const shareUrl = url || originOf();
  const t = SHARE_TEXTS[lang] || SHARE_TEXTS.es;
  const message = `${t.body}\n\n${shareUrl}`;

  const nativeShare = async () => {
    if (typeof navigator === "undefined" || !navigator.share) {
      setOpen(true);
      return;
    }
    setBusy(true);
    try {
      await navigator.share({ title: t.title, text: t.body, url: shareUrl });
    } catch (e) {
      // User cancelled → open our fallback picker anyway so the click is not wasted.
      if (e?.name !== "AbortError") setOpen(true);
    } finally {
      setBusy(false);
    }
  };

  const variantClasses =
    variant === "primary"
      ? "bg-canales-red text-white"
      : variant === "yellow"
      ? "bg-canales-yellow text-black"
      : variant === "blue"
      ? "bg-canales-blue text-white"
      : "bg-white text-black";

  const sizeClasses =
    size === "lg"
      ? "px-5 py-3 text-base"
      : size === "sm"
      ? "px-3 py-1.5 text-xs"
      : "px-4 py-2 text-sm";

  return (
    <>
      <button
        type="button"
        onClick={nativeShare}
        disabled={busy}
        data-testid="share-app-btn"
        className={`brutal-btn inline-flex items-center gap-2 font-display font-black ${variantClasses} ${sizeClasses} disabled:opacity-60 ${className}`}
        aria-label={lang === "es" ? "Compartir la app" : "Share the app"}
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Share2 className="w-4 h-4" />
        )}
        {label ||
          (lang === "es" ? "Compartir la app" : "Share the app")}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="share-app-modal"
        >
          <div
            className="brutal bg-white p-6 w-full max-w-md space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <Share2 className="w-6 h-6 text-canales-red" />
              <h3 className="font-display font-black text-2xl">
                {lang === "es" ? "Compartir la app" : "Share the app"}
              </h3>
            </div>
            <p className="text-sm font-body text-neutral-600">
              {lang === "es"
                ? "Invita a tu equipo con un solo clic. Se compartirá esta liga:"
                : "Invite your team with one click. This URL will be shared:"}
            </p>
            <code className="block brutal-sm bg-canales-cream p-2 font-mono text-xs break-all">
              {shareUrl}
            </code>
            <div className="grid grid-cols-1 gap-2">
              <button
                type="button"
                onClick={() => {
                  openWhatsapp(message);
                  setOpen(false);
                }}
                data-testid="share-app-whatsapp"
                className="brutal-btn bg-[#25D366] text-white px-4 py-3 inline-flex items-center gap-2 font-display font-black text-sm"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </button>
              <button
                type="button"
                onClick={() => {
                  openMail(t.title, message);
                  setOpen(false);
                }}
                data-testid="share-app-email"
                className="brutal-btn bg-white text-black px-4 py-3 inline-flex items-center gap-2 font-display font-black text-sm"
              >
                <Mail className="w-5 h-5 text-canales-blue" />
                {lang === "es" ? "Correo" : "Email"}
              </button>
              <button
                type="button"
                onClick={async () => {
                  const ok = await copyText(message);
                  if (ok) {
                    toast.success(
                      lang === "es"
                        ? "Link copiado al portapapeles"
                        : "Link copied to clipboard"
                    );
                  } else {
                    toast.error(
                      lang === "es" ? "No pude copiar el link" : "Couldn't copy the link"
                    );
                  }
                  setOpen(false);
                }}
                data-testid="share-app-copy"
                className="brutal-btn bg-canales-blue text-white px-4 py-3 inline-flex items-center gap-2 font-display font-black text-sm"
              >
                <Copy className="w-5 h-5" />
                {lang === "es" ? "Copiar link" : "Copy link"}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-full brutal-btn bg-neutral-100 text-black px-4 py-2 text-sm"
            >
              {lang === "es" ? "Cancelar" : "Cancel"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
